import {
  Background,
  ThemeIds,
  accentById,
  accentFit,
  backgroundById,
  bgTone,
  lum,
  primaryById,
  primaryReadable,
} from '@porter/shared-theme';

/** Plain-language account of what the resolver changed, for one theme slot. */
export type ContrastNotice = string;

function pageText(bg: Background) {
  return bgTone(bg).fg === '#FFFFFF' ? 'white' : 'black';
}

/** Null when headings take the primary; otherwise what the page does instead. */
export function primaryContrastNotice(ids: ThemeIds): ContrastNotice | null {
  const bg = backgroundById(ids.backgroundId);
  const primary = primaryById(ids.primaryId);
  if (!bg || !primary || primaryReadable(primary.hex, bg)) return null;
  return `${primary.name} doesn't stand out on ${
    bg.name
  }, so headings use ${pageText(bg)} instead. Buttons still use ${
    primary.name
  }.`;
}

/** Null when links paint the accent as picked; otherwise how it was fitted. */
export function accentContrastNotice(ids: ThemeIds): ContrastNotice | null {
  const bg = backgroundById(ids.backgroundId);
  const accent = accentById(ids.accentId);
  if (!bg || !accent) return null;
  const fit = accentFit(accent.hex, bg);
  if (fit === 'kept') return null;
  if (fit === 'replaced') {
    return `${accent.name} can't be made readable on ${
      bg.name
    }, so links use ${pageText(bg)} instead.`;
  }
  const shade = lum(bgTone(bg).base) > 0.4 ? 'darker' : 'lighter';
  return `${accent.name} shows as a ${shade} shade on ${bg.name} so links stay readable.`;
}

export function contrastNotices(ids: ThemeIds): ContrastNotice[] {
  return [primaryContrastNotice(ids), accentContrastNotice(ids)].filter(
    (notice): notice is ContrastNotice => !!notice,
  );
}
